import { useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { parseUserRole } from './roles';
import { setSentryUser } from '../observability/sentry';

function syncSentryUser(user: User | null | undefined): void {
  if (!user) {
    setSentryUser(null);
    return;
  }
  const role = parseUserRole(user);
  setSentryUser({ id: user.id, role: role ?? undefined });
}

export function useSupabaseSession(): { session: Session | null; loading: boolean } {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: current } }) => {
      setSession(current);
      syncSentryUser(current?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, current) => {
      setSession(current);
      syncSentryUser(current?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  return { session, loading };
}

export function useAuthUser(): User | null {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user: current } }) => {
      setUser(current ?? null);
      syncSentryUser(current ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      syncSentryUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  return user;
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
  setSentryUser(null);
}
